import type { ExperimentPipeline } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { toast } from '@/hooks/use-toast';
import { apiErrorMessage } from '../api-error';

/**
 * The pipelines of the selected website.
 *
 * Separate from usePipelineBoard: the index needs a name, a status and a card's worth of
 * counts, while the board needs three levels of relations per stage. One hook serving
 * both would make opening the list pay for the editor.
 */
export const usePipelines = () => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { website } = useWebsites();
  const websiteId = website?.id;

  const { data: pipelines, isLoading } = useQuery<ExperimentPipeline[]>({
    enabled: !!websiteId,
    queryKey: ['PIPELINES', websiteId],
    queryFn: () => apiClient.get<ExperimentPipeline[]>(`/pipelines/website/${websiteId}`),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['PIPELINES', websiteId] });

  const createPipeline = async (name: string, description = ''): Promise<ExperimentPipeline | null> => {
    if (!websiteId) return null;
    try {
      const pipeline = await apiClient.post<ExperimentPipeline>('/pipelines', { websiteId, name, description });
      invalidate();
      return pipeline;
    } catch (error) {
      toast({
        title: 'Could not create the pipeline',
        description: apiErrorMessage(error),
      });
      return null;
    }
  };

  const deletePipeline = async (pipelineId: number): Promise<boolean> => {
    try {
      await apiClient.delete(`/pipelines/${pipelineId}`);
      invalidate();
      return true;
    } catch (error) {
      // Surfaced verbatim: the only rejection is "pause or cancel this pipeline first",
      // which already says what to do.
      toast({
        title: 'Could not delete the pipeline',
        description: apiErrorMessage(error),
      });
      return false;
    }
  };

  return {
    pipelines: pipelines ?? [],
    isLoading,
    websiteId,
    createPipeline,
    deletePipeline,
  };
};

export type PipelinesManager = ReturnType<typeof usePipelines>;
